import { createPageEntryPoint } from '@atlassian/jira-create-route-entry-point/src/index.tsx';
import { embeddedIssuePreloadLayoutBuilder } from '@atlassian/jira-issue-preload-layout/src/index.tsx';
import { LazyIssueApp } from '@atlassian/jira-lazy-issue-app/src/LazyIssueApp.tsx';
import { MENU_ID } from '@atlassian/jira-navigation-apps-common/src/constants.tsx';
import { forgeProjectModuleResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/forge/index.tsx';
import { staticProjectSidebarConnectAddonsNavigationResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/sidebar/static-project-sidebar-connect-addons/index.tsx';
import { staticProjectSidebarNavigationResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/sidebar/static-project-sidebar/index.tsx';
import { staticSidebarShortcutsResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/sidebar/static-sidebar-shortcuts/index.tsx';
import AsyncSidebarNextGen from '@atlassian/jira-navigation-apps-sidebar-next-gen/src/async.tsx';
import { createEntry } from '@atlassian/jira-route-entry/src/createEntry.tsx';
import { composeLayouts } from '@atlassian/jira-route-layout/src/GlobalComponent.tsx';
import { getNavigationResources } from '@atlassian/jira-router-resources-navigation/src/index.tsx';
import { projectContextResource } from '@atlassian/jira-router-resources-project-context/src/services/project-context/index.tsx';
import { incidentExperimentTraitResource } from '@atlassian/jira-router-resources-software-incidents/src/services/index.tsx';
import { softwareListRoute } from '@atlassian/jira-router-routes-software-list-routes/src/softwareListRoute.tsx';
import { ListSkeleton } from '@atlassian/jira-skeletons/src/ui/list/ListSkeleton.tsx';
import { AsyncHorizontalNavJira } from '@atlassian/jira-horizontal-nav/src/async.tsx';
import { softwareProjectLayout } from '@atlassian/jira-software-project-layout/src/index.tsx';
import { listEntryPoint } from '@atlassian/jira-spa-apps-jsw-list/entrypoint.tsx';
import { jiraHorizontalNavEntrypoint } from '@atlassian/jira-horizontal-nav/entrypoint.tsx';
import { resourceWithCondition2 } from '@atlassian/jira-router-resources-utils/src/utils/resource-with-condition/index.tsx';
import { projectThemeResource } from '@atlassian/jira-router-resources-project-theme/src/index.tsx';
import { getIsSoftwareThemingEnabled } from '@atlassian/jira-project-theme-providers/src/utils/getIsSoftwareThemingEnabled.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { ProjectIssueNavigatorSkeleton } from '@atlassian/jira-skeletons/src/ui/issue-navigator/components/project-app.tsx';
import { NIN_NEW_ISSUE_NAVIGATOR } from '@atlassian/jira-capabilities/src/constants.tsx';
import { getRouteReporting } from '@atlassian/jira-router-routes-issue-navigator-entries/src/common/getRouteReporting.tsx';
import { getCommonProjectResources } from '@atlassian/jira-router-routes-issue-navigator-entries/src/common/getCommonProjectResources.tsx';
import { issueNavigatorPageEntryPoint } from '@atlassian/jira-router-routes-issue-navigator-entries/src/ui/index.tsx';
import { getListProjectResources } from './common/getListProjectResources.tsx';

const listPageEntrypoint = createPageEntryPoint({
	main: listEntryPoint,
	topNavigationMenuId: MENU_ID.PROJECTS,
	horizontalNavigation: jiraHorizontalNavEntrypoint,
});

export const softwareListRouteEntry = createEntry(softwareListRoute, () => ({
	group: 'software',
	skeleton: fg('jira_list_nin_merge') ? ProjectIssueNavigatorSkeleton : ListSkeleton,
	entryPoint() {
		return fg('jira_list_nin_merge') ? issueNavigatorPageEntryPoint : listPageEntrypoint;
	},
	meta: fg('jira_list_nin_merge')
		? {
				capability: NIN_NEW_ISSUE_NAVIGATOR,
				reporting: getRouteReporting(),
			}
		: undefined,
	bifrost: {
		bifrostGroup: 'issue-navigator',
		bundling: {
			packageName:
				'@atlassian/jira-router-routes-software-list-entries/src/softwareListRouteEntry.tsx',
			whichExportToUse: 'softwareListRouteEntry',
		},
		manifestName: 'jira-spa-issue-navigator.json',
		enabled: true,
	},
	layout: composeLayouts(
		softwareProjectLayout,
		embeddedIssuePreloadLayoutBuilder([{ query: ['selectedIssue'] }]),
	),
	navigation: {
		menuId: MENU_ID.PROJECTS,
		sidebar: AsyncSidebarNextGen,
		horizontal: AsyncHorizontalNavJira,
		onlyShowHorizontalOnNav4: true,
	},
	resources: fg('jira_list_nin_merge')
		? getCommonProjectResources()
		: [
				...getNavigationResources(),
				// these have been expanded from getNavigationSidebarProjectResource - please examine whether your route requires these resources in nav4 and remove them with the getWillShowNav4 feature gate if it does not
				incidentExperimentTraitResource,
				projectContextResource,
				forgeProjectModuleResource,
				staticSidebarShortcutsResource,
				staticProjectSidebarNavigationResource,
				staticProjectSidebarConnectAddonsNavigationResource,
				// end of resources expanded from getNavigationSidebarProjectResource
				...getListProjectResources(),
				resourceWithCondition2(() => getIsSoftwareThemingEnabled(), projectThemeResource),
			],
	afterPaint: fg('jira_list_nin_merge') ? undefined : [LazyIssueApp],
	ufoName: fg('jira_list_nin_merge') ? 'software-list-nin' : 'next-gen-list',
	supportsPreviewPanel: true,
	isHydrateRootEnabled: fg('jira_list_nin_merge') ? fg('nin-full-page-hydrate-root') : false,
}));
